'use client';

import { CiChat1 } from 'react-icons/ci';

import { SecondaryButton } from '../../atomic-atoms/buttons/SecondaryButton';
import { ColorDot } from '../../atomic-atoms/ColorDot';

import styles from './ChatButton.module.scss';

type ChatButtonProps = {
  messageCount?: number;
  unreadCount?: number;
  mentionCount?: number;
  disabled?: boolean;
  disabledLabel?: string;
  onClick?: () => void;
};

function hasCount(value?: number) {
  return typeof value === 'number' && value > 0;
}

function pluralize(count: number, singular: string, plural: string) {
  return count === 1 ? singular : plural;
}

export function ChatButton({
  messageCount = 0,
  unreadCount = 0,
  mentionCount = 0,
  disabled = false,
  disabledLabel = 'Chat unavailable',
  onClick,
}: ChatButtonProps) {
  const hasMessages = hasCount(messageCount);
  const hasUnread = hasCount(unreadCount);
  const hasMentions = hasCount(mentionCount);
  const hasAttention = hasUnread || hasMentions;
  const dotLabel = hasMentions
    ? `${mentionCount} chat mentions`
    : hasUnread
      ? `${unreadCount} unread chat messages`
      : `${messageCount} chat ${pluralize(messageCount, 'message', 'messages')}`;
  const dotColor = hasMentions
    ? 'var(--nn-accent-green)'
    : hasUnread
      ? 'var(--nn-color-primary)'
      : 'var(--nn-color-text-muted)';
  const buttonLabel = disabled
    ? disabledLabel
    : hasMentions
      ? `Open chat with ${mentionCount} ${pluralize(mentionCount, 'mention', 'mentions')}`
      : hasUnread
        ? `Open chat with ${unreadCount} unread ${pluralize(unreadCount, 'message', 'messages')}`
        : hasMessages
          ? `Open chat with ${messageCount} ${pluralize(messageCount, 'message', 'messages')}`
          : 'Open chat';

  return (
    <SecondaryButton
      ariaLabel={buttonLabel}
      variant="mini"
      disabled={disabled}
      backgroundOverride={hasAttention ? 'emphasis' : 'theme'}
      onClick={() => {
        if (disabled) {
          return;
        }

        onClick?.();
      }}
    >
      <span
        className={styles.chatButton}
        data-active={hasAttention ? 'true' : undefined}
      >
        <CiChat1 aria-hidden="true" />
        {hasMessages || hasUnread || hasMentions ? (
          <ColorDot
            ariaLabel={dotLabel}
            className={styles.chatButton__dot}
            color={dotColor}
            size="md"
          />
        ) : null}
      </span>
    </SecondaryButton>
  );
}
